import { API_BASE } from './apiBase';
// src/Users.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Pencil, ShieldCheck, Unlock, Ban, Trash2, Plus, X } from 'lucide-react';

const ROLE_LABEL = { employee: 'Employee', projectManager: 'Project Manager', admin: 'Admin', superadmin: 'Superadmin' };
const ROLE_TONE = {
  superadmin: 'bg-red-500/15 text-red-300',
  admin: 'bg-amber-500/15 text-amber-300',
  projectManager: 'bg-indigo-500/15 text-indigo-300',
  employee: 'bg-zinc-700/40 text-zinc-300',
};
const initials = (name) => (name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

function Users() {
  const [users, setUsers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('employee');
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const navigate = useNavigate();
  const currentRole = localStorage.getItem('role') || '';

  // ---- PM permissions editor ----
  const DEFAULT_PERMS = { projects: true, billing: true, inventory: true, employees: true, timecards: true, scheduling: 'readonly', websiteEditor: false };
  const [permUser, setPermUser] = useState(null);  // the PM whose permissions we're editing
  const [permForm, setPermForm] = useState(null);
  const [permSaving, setPermSaving] = useState(false);
  const [pmProjects, setPmProjects] = useState(null); // projects tied to the selected PM (null = loading)

  const openPerms = (user) => {
    setPermUser(user);
    setPermForm({ ...DEFAULT_PERMS, ...(user.permissions || {}), _inventory: user.hasInventoryAccess === true });
    // Load every project this PM is tied to — as primary or co-manager.
    setPmProjects(null);
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/projects`, { headers: { Authorization: token } })
      .then(res => {
        const mine = (res.data || []).filter(p =>
          (p.projectManager?._id === user._id) ||
          (p.additionalManagers || []).some(m => (m.user?._id || m.user) === user._id)
        );
        setPmProjects(mine);
      })
      .catch(() => setPmProjects([]));
  };
  const savePerms = async () => {
    setPermSaving(true);
    try {
      const token = localStorage.getItem('token');
      const { _inventory, ...perms } = permForm;
      const payload = permUser.role === 'employee'
        ? { hasInventoryAccess: _inventory === true } // employees: just the inventory switch
        : { permissions: perms };
      await axios.put(`${API_BASE}/users/${permUser._id}`, payload, { headers: { Authorization: token } });
      setUsers(prev => prev.map(u => u._id === permUser._id ? { ...u, ...payload } : u));
      setPermUser(null);
      setPermForm(null);
    } catch (err) {
      alert('Failed to save permissions.');
    } finally {
      setPermSaving(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !['admin', 'superadmin'].includes(currentRole)) {
      navigate('/login');
      return;
    }
    fetchUsers();
  }, [navigate, currentRole]);

  const fetchUsers = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API_BASE}/users`, {
        headers: { Authorization: token },
      });
      setUsers(res.data);
    } catch (err) {
      console.error('Fetch users error:', err);
      localStorage.clear();
      navigate('/login');
    }
  };

  const startEdit = (user) => {
    setEditingId(user._id);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role,
      password: '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const hasChanges = (originalUser) => {
    return (
      editForm.name !== originalUser.name ||
      editForm.email !== originalUser.email ||
      editForm.role !== originalUser.role ||
      editForm.password !== ''
    );
  };

  const submitEdit = async (id) => {
    const token = localStorage.getItem('token');
    
    try {
      // 1. If a password was entered in the edit form, hit the dedicated password route first
      if (editForm.password && editForm.password.trim() !== '') {
        await axios.put(
          `${API_BASE}/users/${id}/password`,
          { password: editForm.password },
          { headers: { Authorization: token } }
        );
      }

      // 2. Prepare the general profile data (exclude password here)
      const updateData = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        hasInventoryAccess: editForm.hasInventoryAccess,
        isBlocked: editForm.isBlocked,
      };

      // 3. Update the general user information
      const res = await axios.put(
        `${API_BASE}/users/${id}`,
        updateData,
        { headers: { Authorization: token } }
      );

      if (res.data.success) {
        setEditingId(null);
        fetchUsers(); // Refresh the table to show updated info
        alert('User updated successfully');
      }
    } catch (err) {
      console.error('Update error:', err);
      alert(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleBlockToggle = async (user) => {
    if (user.role === 'superadmin') return alert('Cannot block superadmin');
    const action = user.isBlocked ? 'unblock' : 'block';
    if (!window.confirm(`Are you sure you want to ${action} ${user.email}?`)) return;

    try {
      await axios.put(
        `${API_BASE}/users/${user._id}/block`,
        { isBlocked: !user.isBlocked },
        { headers: { Authorization: localStorage.getItem('token') } }
      );
      fetchUsers();
    } catch (err) {
      alert('Failed to update block status');
    }
  };

  const handleDeleteUser = async (user) => {
    if (user.role === 'superadmin') return alert('Cannot delete superadmin');
    const pass = prompt(`DELETE ${user.email}\nEnter your password to confirm:`);
    if (!pass) return;

    try {
      await axios.delete(`${API_BASE}/users/${user._id}`, {
        headers: { Authorization: localStorage.getItem('token') },
        data: { password: pass },
      });
      fetchUsers();
    } catch {
      alert('Wrong password or deletion failed');
    }
  };

  const createUser = async () => {
    if (!newName.trim() || !newEmail.trim() || !newPassword.trim()) {
      setAddError('Name, email and password are required');
      return;
    }

    try {
      await axios.post(
        `${API_BASE}/register`,
        { name: newName.trim(), email: newEmail.trim(), password: newPassword, role: newRole },
        { headers: { Authorization: localStorage.getItem('token') } }
      );

      setNewName('');
      setNewEmail('');
      setNewPassword('');
      setNewRole('employee');
      setShowAddForm(false);
      setAddError('');
      setAddSuccess('User created successfully!');
      fetchUsers();

      setTimeout(() => setAddSuccess(''), 5000);
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleResetAttempts = async (user) => {
    if (!window.confirm(`Unlock ${user.email} and reset their failed login attempts?`)) return;
    try {
      await axios.post(`${API_BASE}/users/${user._id}/reset-attempts`, {}, {
        headers: { Authorization: localStorage.getItem('token') },
      });
      fetchUsers();
    } catch (err) {
      alert('Failed to reset attempts');
    }
  };

  // Helper: Can current user edit this target user?
  const canEditUser = (targetUser) => {
    if (currentRole === 'superadmin') return true;
    // Admins can edit everyone EXCEPT superadmins
    return targetUser.role !== 'superadmin';
  };

  // ---- one calm "⋯" menu per user instead of a wall of buttons ----
  const [menu, setMenu] = useState(null); // { user, x, y }
  const openMenu = (user) => (e) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenu({ user, x: r.right, y: r.bottom + 4 });
  };
  const menuItems = (user) => {
    const isEditable = canEditUser(user);
    const items = [];
    if (isEditable) items.push({ label: 'Edit', icon: Pencil, run: () => startEdit(user) });
    if (currentRole === 'superadmin' && ['projectManager', 'admin', 'employee'].includes(user.role))
      items.push({ label: 'Permissions', icon: ShieldCheck, run: () => openPerms(user) });
    if (currentRole === 'superadmin' && user.isLocked)
      items.push({ label: 'Reset login attempts', icon: Unlock, run: () => handleResetAttempts(user), tone: 'text-amber-300' });
    if (currentRole === 'superadmin' && user.role !== 'superadmin') {
      items.push({ label: user.isBlocked ? 'Unblock' : 'Block', icon: Ban, run: () => handleBlockToggle(user), tone: user.isBlocked ? '' : 'text-red-300' });
      items.push({ label: 'Delete', icon: Trash2, run: () => handleDeleteUser(user), tone: 'text-red-400' });
    }
    return items;
  };

  // Status pill: Blocked > Locked > Active.
  const StatusPill = ({ user }) => user.isBlocked
    ? <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-red-500/15 text-red-300">Blocked</span>
    : user.isLocked
      ? <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300">Locked</span>
      : <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">Active</span>;

  const inputCls = "w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60";

  // Inline edit fields — shared by the desktop row and the mobile card.
  // Called as a FUNCTION ({renderEditFields(user)}), not a component, so the
  // inputs never remount mid-keystroke.
  const renderEditFields = (user) => (
    <div className="grid grid-cols-2 max-md:grid-cols-1 gap-2.5">
      <input className={inputCls} placeholder="Name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
      <input className={inputCls} type="email" placeholder="Email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
      <select className={inputCls} value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
        <option value="employee">Employee</option>
        <option value="projectManager">Project Manager</option>
        <option value="admin">Admin</option>
        <option value="superadmin">Superadmin</option>
      </select>
      <input className={inputCls} type="password" placeholder="New password (optional)" value={editForm.password || ''} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
      <div className="col-span-2 max-md:col-span-1 flex justify-end gap-2 mt-1">
        <button onClick={cancelEdit} className="px-4 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs font-medium">Cancel</button>
        <button
          onClick={() => (hasChanges(user) ? submitEdit(user._id) : cancelEdit())}
          className={`px-4 py-1.5 rounded-lg text-xs font-medium text-white ${hasChanges(user) ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-gray-600'}`}
        >
          Save
        </button>
      </div>
    </div>
  );

  if (!['admin', 'superadmin'].includes(currentRole)) return null;

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 max-md:p-3">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-8 max-md:mb-5">
        <h1 className="text-3xl max-md:text-xl font-bold tracking-tight">User Management</h1>
        {!permUser && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className={`px-4 py-2 max-md:px-3 max-md:py-1.5 rounded-lg text-sm max-md:text-xs font-medium transition-colors flex items-center gap-1.5 ${
              showAddForm ? 'bg-gray-700 hover:bg-gray-600 text-zinc-200' : 'bg-indigo-600 hover:bg-indigo-500 text-white'
            }`}
          >
            {showAddForm ? <><X className="w-3.5 h-3.5" /> Cancel</> : <><Plus className="w-3.5 h-3.5" /> Add User</>}
          </button>
        )}
      </div>

      {/* Add User Form */}
      {!permUser && showAddForm && (
        <div className="mb-8 bg-gray-900 rounded-2xl border border-gray-800/50 p-6 max-md:p-4 shadow-2xl">
          <h3 className="text-lg font-bold mb-4">Create New User</h3>
          <div className="grid grid-cols-2 max-md:grid-cols-1 gap-3">
            <input placeholder="Full Name *" value={newName} onChange={(e) => setNewName(e.target.value)} className={inputCls} required />
            <input type="email" placeholder="Email *" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={inputCls} required />
            <input type="password" placeholder="Password *" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputCls} required />
            <select value={newRole} onChange={(e) => setNewRole(e.target.value)} className={inputCls}>
              <option value="employee">Employee</option>
              <option value="projectManager">Project Manager</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Superadmin</option>
            </select>
          </div>
          <div className="flex justify-end mt-4">
            <button onClick={createUser} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition-colors">
              Create User
            </button>
          </div>
          {addError && <p className="text-red-400 text-sm mt-3">{addError}</p>}
          {addSuccess && <p className="text-green-400 text-sm mt-3 font-medium">{addSuccess}</p>}
        </div>
      )}

      {/* PM Permission card — replaces the table when a PM is selected */}
      {permUser && permForm ? (
        <div className="bg-gray-900 rounded-2xl border border-gray-800/50 shadow-2xl p-8 max-md:p-4 max-w-2xl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-indigo-900/60 flex items-center justify-center text-indigo-200 font-bold">
                {(permUser.name || 'PM').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <div>
                <p className="text-white font-bold text-lg">{permUser.name}</p>
                <p className="text-zinc-500 text-xs">{ROLE_LABEL[permUser.role] || permUser.role} · permissions</p>
              </div>
            </div>
            <button onClick={() => { setPermUser(null); setPermForm(null); }} className="text-zinc-400 hover:text-white text-sm">← Back to users</button>
          </div>

          <div className="space-y-1 divide-y divide-gray-800/60">
            {permUser.role === 'employee' ? (
              <div className="flex items-center justify-between py-3 gap-4">
                <div>
                  <span className="text-zinc-200 text-sm">Inventory access</span>
                  <p className="text-zinc-500 text-xs mt-0.5">Employees have no inventory access by default — flip this on to show them the Inventory section.</p>
                </div>
                <button
                  onClick={() => setPermForm({ ...permForm, _inventory: !permForm._inventory })}
                  className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${permForm._inventory ? 'bg-indigo-600' : 'bg-gray-700'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${permForm._inventory ? 'translate-x-5' : ''}`} />
                </button>
              </div>
            ) : permUser.role === 'admin' ? (
              <div className="flex items-center justify-between py-3 gap-4">
                <div>
                  <span className="text-zinc-200 text-sm">Website Editor</span>
                  <p className="text-zinc-500 text-xs mt-0.5">Shows the Portal / Website toggle and allows editing the public website.</p>
                </div>
                <button
                  onClick={() => setPermForm({ ...permForm, websiteEditor: !permForm.websiteEditor })}
                  className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${permForm.websiteEditor ? 'bg-indigo-600' : 'bg-gray-700'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${permForm.websiteEditor ? 'translate-x-5' : ''}`} />
                </button>
              </div>
            ) : (
              <>
                {[
                  ['projects', 'Access to Projects'],
                  ['billing', 'Access to Billing'],
                  ['inventory', 'Access to Inventory'],
                  ['employees', 'Access to Employees'],
                  ['timecards', 'Access to Timecards'],
                ].map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between py-3">
                    <span className="text-zinc-200 text-sm">{label}</span>
                    <button
                      onClick={() => setPermForm({ ...permForm, [key]: !permForm[key] })}
                      className={`relative w-11 h-6 rounded-full transition-colors ${permForm[key] ? 'bg-indigo-600' : 'bg-gray-700'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${permForm[key] ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                ))}
                {/* Scheduling is tri-state: read-only or hidden */}
                <div className="flex items-center justify-between py-3">
                  <span className="text-zinc-200 text-sm">Employees &gt; Scheduling</span>
                  <select
                    value={permForm.scheduling}
                    onChange={(e) => setPermForm({ ...permForm, scheduling: e.target.value })}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-zinc-200"
                  >
                    <option value="readonly">Read only</option>
                    <option value="hidden">Hidden</option>
                  </select>
                </div>
              </>
            )}
          </div>

          {/* Projects tied to this PM — primary or co-manager (PM only) */}
          {permUser.role === 'projectManager' && (
          <div className="mt-8">
            <h4 className="text-indigo-400 text-[10px] font-black uppercase mb-3">
              Assigned Projects{Array.isArray(pmProjects) ? ` (${pmProjects.length})` : ''}
            </h4>
            {pmProjects === null ? (
              <p className="text-zinc-600 text-xs">Loading projects…</p>
            ) : pmProjects.length === 0 ? (
              <p className="text-zinc-600 text-xs">No projects assigned to this manager.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {pmProjects.map(p => {
                  const isPrimary = p.projectManager?._id === permUser._id;
                  return (
                    <div
                      key={p._id}
                      onClick={() => navigate(`/projects/${p._id}`)}
                      className="flex items-center justify-between gap-3 px-4 py-2.5 bg-gray-800/40 hover:bg-gray-800 border border-gray-800/60 hover:border-indigo-500/40 rounded-xl cursor-pointer transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-zinc-200 text-sm font-semibold truncate">
                          #{p.poNumber} · {p.title}
                        </p>
                        <p className="text-zinc-500 text-xs">{p.status}</p>
                      </div>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full flex-shrink-0 ${
                        isPrimary ? 'bg-indigo-900/60 text-indigo-300' : 'bg-gray-700/60 text-zinc-400'
                      }`}>
                        {isPrimary ? 'Primary' : 'Co-PM'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}

          <div className="flex gap-3 mt-8">
            <button onClick={() => { setPermUser(null); setPermForm(null); }} className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium">Cancel</button>
            <button onClick={savePerms} disabled={permSaving} className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-medium disabled:opacity-50">
              {permSaving ? 'Saving…' : 'Save permissions'}
            </button>
          </div>
        </div>
      ) : (
      <>
      {/* Desktop table */}
      <div className="overflow-x-auto bg-gray-900 rounded-2xl border border-gray-800/50 shadow-2xl max-md:hidden">
        <table className="min-w-full divide-y divide-gray-800/60">
          <thead className="bg-gray-950/30">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">User</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Role</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/40">
            {users.map((user) => (
              <React.Fragment key={user._id}>
                <tr className={`transition-colors ${user.isBlocked ? 'bg-red-950/20 hover:bg-red-950/40' : 'hover:bg-gray-800/40'}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-indigo-900/60 flex items-center justify-center text-indigo-200 text-xs font-bold shrink-0">
                        {initials(user.name)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-zinc-100 truncate">{user.name}</p>
                        <p className="text-xs text-zinc-500 truncate">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${ROLE_TONE[user.role] || ROLE_TONE.employee}`}>
                      {ROLE_LABEL[user.role] || user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <StatusPill user={user} />
                      {user.isBlocked && user.tempPassword && (
                        <span className="text-[10px] font-mono text-red-300">temp: {user.tempPassword}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={openMenu(user)}
                      className="p-2 rounded-lg hover:bg-gray-800 text-zinc-500 hover:text-zinc-200 transition-colors"
                      title="Actions"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                {editingId === user._id && (
                  <tr className="bg-gray-950/40">
                    <td colSpan={4} className="px-6 py-4">{renderEditFields(user)}</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {users.map((user) => (
          <div key={user._id} className={`bg-gray-900 border rounded-xl p-3.5 ${user.isBlocked ? 'border-red-900/60' : 'border-gray-800/60'}`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-indigo-900/60 flex items-center justify-center text-indigo-200 text-xs font-bold shrink-0">
                {initials(user.name)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-zinc-100 truncate">{user.name}</p>
                <p className="text-xs text-zinc-500 break-all">{user.email}</p>
              </div>
              <button onClick={openMenu(user)} className="p-2 rounded-lg hover:bg-gray-800 text-zinc-500 shrink-0" title="Actions">
                <MoreVertical className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-2 mt-2.5 flex-wrap">
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${ROLE_TONE[user.role] || ROLE_TONE.employee}`}>
                {ROLE_LABEL[user.role] || user.role}
              </span>
              <StatusPill user={user} />
              {user.isBlocked && user.tempPassword && (
                <span className="text-[10px] font-mono text-red-300">temp: {user.tempPassword}</span>
              )}
            </div>
            {editingId === user._id && <div className="mt-3">{renderEditFields(user)}</div>}
          </div>
        ))}
      </div>
      </>
      )}

      {/* ⋯ actions menu — fixed so table overflow can't clip it */}
      {menu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenu(null)} onContextMenu={(e) => { e.preventDefault(); setMenu(null); }} />
          <div
            className="fixed z-50 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-1 min-w-[12rem]"
            style={{ left: Math.min(menu.x - 190, (window.innerWidth || 800) - 200), top: Math.min(menu.y, (window.innerHeight || 600) - menuItems(menu.user).length * 38 - 16) }}
          >
            {menuItems(menu.user).length === 0 && (
              <p className="px-3 py-2 text-xs text-zinc-600">No actions available</p>
            )}
            {menuItems(menu.user).map((it) => (
              <button
                key={it.label}
                onClick={() => { setMenu(null); it.run(); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-800 text-left text-sm ${it.tone || 'text-zinc-300'}`}
              >
                <it.icon className="w-3.5 h-3.5" /> {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default Users;